/**
 * Redaction Utility
 *
 * Sanitizes sensitive data from log payloads and RUM context.
 * Prevents PII and secrets from being sent to external observability platforms.
 */

/**
 * Sensitive field patterns to redact (case-insensitive).
 * These patterns match common sensitive field names.
 */
const SENSITIVE_PATTERNS = [
  /password/i,
  /secret/i,
  /^token$/i,
  /api[_-]?key/i,
  /^auth$/i,
  /^credential$/i,
  /ssn/i,
  /social[_-]?security/i,
  /credit[_-]?card/i,
  /card[_-]?number/i,
  /cvv/i,
  /^pin$/i,
  /private[_-]?key/i,
  /access[_-]?token/i,
  /refresh[_-]?token/i,
  /bearer/i,
  /authorization/i,
  /id[_-]?token/i,
  /session[_-]?token/i,
];

/**
 * Redaction placeholder used to replace sensitive values.
 */
const REDACTED = '[REDACTED]';

/**
 * Checks if a field name matches any sensitive pattern.
 */
export const isSensitiveField = (fieldName: string): boolean => {
  return SENSITIVE_PATTERNS.some((pattern) => pattern.test(fieldName));
};

/**
 * Recursively redacts sensitive fields from an object.
 * Returns a new object with sensitive values replaced.
 *
 * @param obj - The object to redact
 * @param maxDepth - Maximum recursion depth (default: 10)
 * @returns A new object with sensitive fields redacted
 */
export const redactSensitiveFields = <T>(obj: T, maxDepth = 10): T => {
  if (maxDepth <= 0) {
    return '[MAX_DEPTH]' as T;
  }

  if (obj === null || obj === undefined) {
    return obj;
  }

  if (typeof obj !== 'object') {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => redactSensitiveFields(item, maxDepth - 1)) as T;
  }

  const result: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(obj)) {
    if (isSensitiveField(key)) {
      result[key] = REDACTED;
    } else if (typeof value === 'object' && value !== null) {
      result[key] = redactSensitiveFields(value, maxDepth - 1);
    } else if (typeof value === 'string') {
      result[key] = redactStringValue(value);
    } else {
      result[key] = value;
    }
  }

  return result as T;
};

/**
 * Redacts a single string value if it appears to contain sensitive data.
 * This is a simple heuristic check for common patterns.
 */
export const redactStringValue = (value: string): string => {
  // Check for JWT-like patterns (three base64 segments separated by dots)
  if (/^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(value)) {
    return REDACTED;
  }

  // Check for API key-like patterns (long alphanumeric strings with mixed case).
  // Pure lowercase hex strings are excluded — they are likely deterministic
  // hashes (e.g. SHA-256 from hashForObservability), not secrets.
  if (/^[A-Za-z0-9]{32,}$/.test(value) && !/^[a-f0-9]+$/.test(value)) {
    return REDACTED;
  }

  return value;
};

/**
 * Produces a deterministic SHA-256 hex hash of a string value.
 * Normalizes input (lowercase + trim) so the same logical value
 * always produces the same hash regardless of casing or whitespace.
 *
 * Used to create non-PII identifiers for pre-auth observability
 * (e.g. hashing an email so support can search Datadog without
 * storing raw PII in logs/RUM events).
 */
export const hashForObservability = async (value: string): Promise<string> => {
  const normalized = value.toLowerCase().trim();
  const encoded = new TextEncoder().encode(normalized);
  const buffer = await crypto.subtle.digest('SHA-256', encoded);
  const bytes = new Uint8Array(buffer);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
};

/**
 * Creates a safe log context by redacting sensitive fields.
 * Handles errors gracefully by returning an empty object on failure.
 */
export const createSafeContext = (
  context: Record<string, unknown>
): Record<string, unknown> => {
  try {
    return redactSensitiveFields(context);
  } catch {
    // If redaction fails, return empty context to avoid leaking data
    return { redactionError: true };
  }
};
